import type { CheckpointCrossing, CheckpointTraceResult, Point } from './StrokeEvaluator';

type ReplayCheckpoint = Pick<CheckpointCrossing, 'point' | 'checkpointIndex' | 'score' | 'distance'>;

type StrokeAttemptConfig = {
	start: Point;
	maxInk: number;
	inputDeadzone: number;
	skipWarningDistance?: number;
	evaluateSegment: (
		from: Point,
		to: Point,
		maxMovementProgress: number,
		endpointOnly?: boolean,
	) => CheckpointTraceResult;
};

export type StrokeAttemptResult = CheckpointTraceResult & {
	position: Point;
	outOfInk: boolean;
	skipWarning: boolean;
};

export class StrokeAttempt {
	private inkUsed = 0;
	private started = false;
	private lastScoredDistance = 0;
	private skipWarningConsumed = false;
	private readonly crossings: ReplayCheckpoint[] = [];
	private readonly points: [Point, ...Point[]];

	constructor(private readonly config: StrokeAttemptConfig) {
		this.points = [{ ...config.start }];
	}

	startAt(point: Point) {
		if (this.started) {
			return false;
		}
		this.started = true;
		this.points[0] = { ...point };
		return true;
	}

	getPoints(): readonly [Point, ...Point[]] {
		return this.points;
	}

	getCrossings(): readonly ReplayCheckpoint[] {
		return this.crossings;
	}

	trace(from: Point, to: Point): StrokeAttemptResult | null {
		const movementLength = Math.hypot(to.x - from.x, to.y - from.y);

		const remainingInk = Math.max(0, this.config.maxInk - this.inkUsed);
		const acceptedProgress = movementLength === 0 ? 0 : Math.min(1, remainingInk / movementLength);
		const endpointOnly = movementLength < this.config.inputDeadzone;
		const result = this.config.evaluateSegment(from, to, acceptedProgress, endpointOnly);
		if (endpointOnly && !result.completed) return null;
		this.started = true;
		const renderedProgress = result.completion?.movementProgress ?? acceptedProgress;
		const acceptedPosition = result.completion?.point ?? {
			x: from.x + (to.x - from.x) * renderedProgress,
			y: from.y + (to.y - from.y) * renderedProgress,
		};
		const position = result.completion?.endpoint ?? acceptedPosition;

		let skipWarning = false;
		for (const { point, movementProgress, checkpointIndex, score } of result.crossings) {
			const distance = this.inkUsed + movementLength * movementProgress;
			if (this.consumeSkipWarning(distance)) skipWarning = true;
			this.lastScoredDistance = distance;
			this.skipWarningConsumed = false;
			this.crossings.push({
				point,
				checkpointIndex,
				score,
				distance,
			});
		}
		this.inkUsed += movementLength * renderedProgress;
		if (this.consumeSkipWarning(this.inkUsed)) skipWarning = true;
		if (renderedProgress > 0) this.points.push(acceptedPosition);
		if (position.x !== acceptedPosition.x || position.y !== acceptedPosition.y) this.points.push({ ...position });

		return { ...result, position, skipWarning, outOfInk: !result.completed && movementLength >= remainingInk };
	}

	private consumeSkipWarning(distance: number) {
		const threshold = this.config.skipWarningDistance;
		if (threshold === undefined || this.skipWarningConsumed || distance - this.lastScoredDistance < threshold) {
			return false;
		}
		this.skipWarningConsumed = true;
		return true;
	}
}
